import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { linkedinAPI } from '../lib/api';
import {
  Linkedin, Loader2, CheckCircle, ChevronRight, ChevronDown,
  Camera, Type, FileText, Users, Star, MessageSquare,
  Award, Briefcase, Layout, Clock, ArrowRight, ExternalLink,
  AlertCircle, TrendingUp, Target, Upload, Plus, Trash2, Link2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const STEP_ICONS = {
  photo: Camera, headline: Type, about: FileText, connections: Users,
  recommendations: Star, posting: MessageSquare, skills: Award,
  featured: Layout, experience: Briefcase, complete_assessment: Target,
};

const PRIORITY_COLORS = {
  HIGH: { bg: '#DC262612', text: '#DC2626', border: '#DC262630' },
  MEDIUM: { bg: '#F5841F12', text: '#F5841F', border: '#F5841F30' },
  LOW: { bg: '#0D948812', text: '#0D9488', border: '#0D948830' },
};

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.4 },
});

const TABS = [
  { id: 'optimizer', label: 'Optimizer', icon: TrendingUp },
  { id: 'import', label: 'Import Data', icon: Upload },
];

export default function LinkedInOptimizerPage() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('optimizer');
  const [questions, setQuestions] = useState([]);
  const [existingAnswers, setExistingAnswers] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [optimizer, setOptimizer] = useState(null);
  const [expandedStep, setExpandedStep] = useState(null);
  const [showAssessment, setShowAssessment] = useState(false);

  // Import state
  const [importData, setImportData] = useState({
    linkedin_url: '',
    headline: '',
    about: '',
    location: '',
    education_summary: '',
    skills: [],
    experience: [],
  });
  const [newSkill, setNewSkill] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [qRes, profRes] = await Promise.all([
          linkedinAPI.questions(),
          linkedinAPI.profile(),
        ]);
        setQuestions(qRes.data.questions || []);
        setExistingAnswers(qRes.data.existing_answers || null);
        setCompleted(profRes.data.completed);

        if (qRes.data.existing_answers) {
          setAnswers(qRes.data.existing_answers);
        }

        if (profRes.data.completed) {
          setResult({
            score: profRes.data.score,
            grade: profRes.data.grade,
            confidence: profRes.data.confidence,
          });
          const optRes = await linkedinAPI.optimizer();
          setOptimizer(optRes.data);
        }

        // Load previous import data
        try {
          const impRes = await linkedinAPI.getImportData();
          if (impRes.data?.imported && impRes.data.data) {
            setImportData(prev => ({
              ...prev,
              ...impRes.data.data,
              skills: impRes.data.data.skills || [],
              experience: impRes.data.data.experience || [],
            }));
            setImportResult({ imported_at: impRes.data.imported_at });
          }
        } catch {}
      } catch (err) {
        console.error('LinkedIn load error:', err);
        toast.error('Failed to load LinkedIn assessment');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handleSubmit = async () => {
    const unanswered = questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${unanswered.length} remaining question(s)`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await linkedinAPI.submit(answers);
      setResult(res.data);
      setCompleted(true);
      const optRes = await linkedinAPI.optimizer();
      setOptimizer(optRes.data);
      setShowAssessment(false);
      toast.success('LinkedIn assessment complete!');
    } catch (err) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const gradeColor = (g) => {
    if (!g) return '#718096';
    if (g.startsWith('A')) return '#16A34A';
    if (g.startsWith('B')) return '#84CC16';
    if (g.startsWith('C')) return '#EAB308';
    if (g.startsWith('D')) return '#F97316';
    return '#DC2626';
  };

  // Import helpers
  const updateImport = (field, value) => setImportData(prev => ({ ...prev, [field]: value }));
  const addExperience = () => setImportData(prev => ({
    ...prev,
    experience: [...prev.experience, { title: '', company: '', start_date: '', end_date: '', description: '' }],
  }));
  const updateExperience = (idx, field, value) => setImportData(prev => ({
    ...prev,
    experience: prev.experience.map((e, i) => i === idx ? { ...e, [field]: value } : e),
  }));
  const removeExperience = (idx) => setImportData(prev => ({
    ...prev,
    experience: prev.experience.filter((_, i) => i !== idx),
  }));
  const addSkill = () => {
    if (newSkill.trim() && !importData.skills.includes(newSkill.trim())) {
      setImportData(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill('');
    }
  };
  const removeSkill = (skill) => setImportData(prev => ({
    ...prev,
    skills: prev.skills.filter(s => s !== skill),
  }));
  const handleImport = async () => {
    if (!importData.headline && !importData.about && importData.skills.length === 0 && importData.experience.length === 0) {
      toast.error('Please fill in at least one field to import');
      return;
    }
    setImporting(true);
    try {
      const res = await linkedinAPI.importData(importData);
      setImportResult(res.data);
      toast.success(res.data.message || 'LinkedIn data imported successfully!');
    } catch {
      toast.error('Failed to import LinkedIn data');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0D9488' }} />
      </div>
    );
  }

  const answeredCount = questions.filter(q => answers[q.id]).length;

  return (
    <div data-testid="linkedin-optimizer-page" className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#0077B5' }}>
          LinkedIn Optimizer
        </p>
        <h1 className="text-2xl font-extrabold mt-1" style={{ color: 'var(--t-text-heading)' }}>
          Optimize Your LinkedIn Profile
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--t-text-muted)' }}>
          Assess your LinkedIn presence, import your data, and get a personalized improvement plan.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold flex-1 justify-center transition-all"
              style={{
                background: isActive ? '#0077B5' : 'transparent',
                color: isActive ? '#fff' : 'var(--t-text-muted)',
              }}
              data-testid={`linkedin-tab-${tab.id}`}>
              <Icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'import' ? (
        /* ── Import Data Tab ── */
        <motion.div key="import" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          {importResult && (
            <div className="nx-card p-4 mb-5 flex items-center gap-3" style={{ borderColor: 'rgba(13,148,136,0.3)' }}>
              <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#0D9488' }} />
              <div>
                <p className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Data Imported</p>
                <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                  {importResult.message || `Last imported: ${new Date(importResult.imported_at).toLocaleDateString()}`}
                </p>
              </div>
            </div>
          )}

          <div className="nx-card p-5 mb-5" data-testid="linkedin-import-form">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#0077B512' }}>
                <Link2 className="w-5 h-5" style={{ color: '#0077B5' }} />
              </div>
              <div>
                <h2 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>LinkedIn Profile Import</h2>
                <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                  Paste your details below to auto-populate your Kairera profile
                </p>
              </div>
            </div>

            {/* URL & Headline */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'var(--t-text-muted)' }}>
                  LinkedIn URL
                </label>
                <Input value={importData.linkedin_url} onChange={e => updateImport('linkedin_url', e.target.value)}
                  placeholder="https://linkedin.com/in/yourname" className="h-9 text-xs"
                  style={{ color: 'var(--t-text)', background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}
                  data-testid="import-linkedin-url" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'var(--t-text-muted)' }}>
                  Headline
                </label>
                <Input value={importData.headline} onChange={e => updateImport('headline', e.target.value)}
                  placeholder="Senior Software Engineer | Cloud Architecture"
                  className="h-9 text-xs" style={{ color: 'var(--t-text)', background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}
                  data-testid="import-headline" />
              </div>
            </div>

            {/* Location */}
            <div className="mb-4">
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'var(--t-text-muted)' }}>
                Location
              </label>
              <Input value={importData.location} onChange={e => updateImport('location', e.target.value)}
                placeholder="San Francisco, CA" className="h-9 text-xs"
                style={{ color: 'var(--t-text)', background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}
                data-testid="import-location" />
            </div>

            {/* About */}
            <div className="mb-4">
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'var(--t-text-muted)' }}>
                About / Summary
              </label>
              <textarea value={importData.about} onChange={e => updateImport('about', e.target.value)}
                placeholder="Paste your LinkedIn About section here..."
                rows={4} className="w-full p-3 rounded-lg text-xs resize-none"
                style={{ color: 'var(--t-text)', background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}
                data-testid="import-about" />
            </div>

            {/* Skills */}
            <div className="mb-4">
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'var(--t-text-muted)' }}>
                Skills
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {importData.skills.map((s, i) => (
                  <span key={i} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold"
                    style={{ background: '#0077B510', color: '#0077B5', border: '1px solid #0077B520' }}>
                    {s}
                    <button onClick={() => removeSkill(s)} className="hover:opacity-60">
                      <Trash2 className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newSkill} onChange={e => setNewSkill(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  placeholder="Type a skill and press Enter" className="h-8 text-xs flex-1"
                  style={{ color: 'var(--t-text)', background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}
                  data-testid="import-skill-input" />
                <Button onClick={addSkill} className="h-8 px-3 text-[10px] font-bold rounded-lg"
                  style={{ background: '#0077B515', color: '#0077B5', border: '1px solid #0077B520' }}
                  data-testid="import-add-skill-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>

            {/* Experience */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>
                  Experience
                </label>
                <Button onClick={addExperience} className="h-7 px-2.5 text-[9px] font-bold rounded-lg"
                  style={{ background: '#0077B515', color: '#0077B5', border: '1px solid #0077B520' }}
                  data-testid="import-add-exp-btn">
                  <Plus className="w-3 h-3 mr-0.5" /> Add Role
                </Button>
              </div>
              <div className="space-y-3">
                {importData.experience.map((exp, i) => (
                  <div key={i} className="p-3 rounded-xl relative" data-testid={`import-exp-${i}`}
                    style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                    <button onClick={() => removeExperience(i)}
                      className="absolute top-2 right-2 p-1 rounded-md hover:opacity-60"
                      style={{ color: 'var(--t-text-muted)' }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Input value={exp.title} onChange={e => updateExperience(i, 'title', e.target.value)}
                        placeholder="Job Title" className="h-8 text-xs"
                        style={{ color: 'var(--t-text)', background: 'var(--t-bg-card-solid)', borderColor: 'var(--t-border)' }} />
                      <Input value={exp.company} onChange={e => updateExperience(i, 'company', e.target.value)}
                        placeholder="Company" className="h-8 text-xs"
                        style={{ color: 'var(--t-text)', background: 'var(--t-bg-card-solid)', borderColor: 'var(--t-border)' }} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Input value={exp.start_date} onChange={e => updateExperience(i, 'start_date', e.target.value)}
                        placeholder="Start (e.g., Jan 2020)" className="h-8 text-xs"
                        style={{ color: 'var(--t-text)', background: 'var(--t-bg-card-solid)', borderColor: 'var(--t-border)' }} />
                      <Input value={exp.end_date} onChange={e => updateExperience(i, 'end_date', e.target.value)}
                        placeholder="End (or Present)" className="h-8 text-xs"
                        style={{ color: 'var(--t-text)', background: 'var(--t-bg-card-solid)', borderColor: 'var(--t-border)' }} />
                    </div>
                    <textarea value={exp.description} onChange={e => updateExperience(i, 'description', e.target.value)}
                      placeholder="Key responsibilities and achievements..." rows={2}
                      className="w-full p-2 rounded-lg text-xs resize-none"
                      style={{ color: 'var(--t-text)', background: 'var(--t-bg-card-solid)', border: '1px solid var(--t-border)' }} />
                  </div>
                ))}
                {importData.experience.length === 0 && (
                  <div className="text-center p-4 rounded-xl" style={{ background: 'var(--t-bg-section)', border: '1px dashed var(--t-border)' }}>
                    <Briefcase className="w-5 h-5 mx-auto mb-1" style={{ color: 'var(--t-text-muted)' }} />
                    <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>No experience added yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Education */}
            <div className="mb-5">
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'var(--t-text-muted)' }}>
                Education Summary
              </label>
              <Input value={importData.education_summary} onChange={e => updateImport('education_summary', e.target.value)}
                placeholder="e.g., BS Computer Science, MIT 2018" className="h-9 text-xs"
                style={{ color: 'var(--t-text)', background: 'var(--t-bg-section)', borderColor: 'var(--t-border)' }}
                data-testid="import-education" />
            </div>

            {/* Import Button */}
            <Button onClick={handleImport} disabled={importing}
              className="w-full h-11 font-bold rounded-xl text-sm"
              style={{ background: '#0077B5', color: '#fff' }}
              data-testid="import-submit-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? 'Importing...' : 'Import to Profile'}
            </Button>
          </div>

          {/* Import info */}
          <div className="nx-card p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#0077B5' }} />
              <div>
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--t-text-heading)' }}>
                  How to get your LinkedIn data
                </p>
                <ol className="text-[10px] space-y-1" style={{ color: 'var(--t-text-muted)' }}>
                  <li>1. Open your LinkedIn profile in a browser</li>
                  <li>2. Copy your headline, about section, and experience details</li>
                  <li>3. Paste them into the fields above</li>
                  <li>4. Your data will be synced to your Kairera profile for resume building and career analysis</li>
                </ol>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
      /* ── Optimizer Tab ── */
      <div>

      {/* Score Card (if completed) */}
      {completed && result && (
        <motion.div {...fadeIn(0)} className="nx-card p-6 mb-6" data-testid="linkedin-score-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: `${gradeColor(result.grade)}12`, border: `2px solid ${gradeColor(result.grade)}` }}>
                <span className="text-2xl font-black" style={{ color: gradeColor(result.grade) }}>{result.grade}</span>
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>
                  LinkedIn Score: {result.score}/10
                </p>
                <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                  Confidence: {result.confidence}
                </p>
              </div>
            </div>
            <Button onClick={() => setShowAssessment(!showAssessment)} variant="outline"
              className="h-9 px-4 text-xs font-semibold rounded-lg"
              style={{ borderColor: 'var(--t-border)', color: 'var(--t-text)' }}
              data-testid="retake-assessment-btn">
              {showAssessment ? 'Hide Assessment' : 'Retake Assessment'}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Assessment Form */}
      <AnimatePresence>
        {(!completed || showAssessment) && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden">
            <div className="nx-card p-6 mb-6" data-testid="linkedin-assessment-form">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-bold" style={{ color: 'var(--t-text-heading)' }}>
                    LinkedIn Self-Assessment
                  </h2>
                  <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                    Answer {questions.length} questions about your LinkedIn profile
                  </p>
                </div>
                <span className="text-xs font-bold px-3 py-1 rounded-full"
                  style={{ background: 'rgba(13,148,136,0.1)', color: '#0D9488' }}>
                  {answeredCount}/{questions.length}
                </span>
              </div>

              {/* Progress bar */}
              <div className="h-1.5 rounded-full mb-6 overflow-hidden" style={{ background: 'var(--t-border)' }}>
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${(answeredCount / questions.length) * 100}%`, background: '#0077B5' }} />
              </div>

              <div className="space-y-5">
                {questions.map((q, i) => (
                  <motion.div key={q.id} {...fadeIn(i * 0.03)}>
                    <p className="text-sm font-semibold mb-2" style={{ color: 'var(--t-text-heading)' }}>
                      <span className="text-[10px] font-bold mr-2 px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(0,119,181,0.08)', color: '#0077B5' }}>
                        {i + 1}
                      </span>
                      {q.question}
                    </p>
                    {q.help && (
                      <p className="text-[11px] mb-2" style={{ color: 'var(--t-text-muted)' }}>{q.help}</p>
                    )}

                    {q.type === 'text' ? (
                      <Input
                        value={answers[q.id] || ''}
                        onChange={e => handleAnswer(q.id, e.target.value)}
                        placeholder={q.placeholder}
                        className="h-10 text-sm"
                        data-testid={`linkedin-q-${q.id}`}
                        style={{ color: 'var(--t-text)' }}
                      />
                    ) : (
                      <div className="grid gap-2">
                        {q.options?.map(opt => {
                          const selected = answers[q.id] === opt.value;
                          return (
                            <button key={opt.value}
                              onClick={() => handleAnswer(q.id, opt.value)}
                              className="flex items-center gap-3 p-3 rounded-xl text-left transition-all text-sm"
                              style={{
                                background: selected ? 'rgba(0,119,181,0.08)' : 'var(--t-bg-section)',
                                border: `1.5px solid ${selected ? '#0077B5' : 'var(--t-border)'}`,
                                color: selected ? '#0077B5' : 'var(--t-text)',
                              }}
                              data-testid={`linkedin-opt-${q.id}-${opt.value}`}>
                              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{
                                  border: `2px solid ${selected ? '#0077B5' : 'var(--t-border)'}`,
                                  background: selected ? '#0077B5' : 'transparent',
                                }}>
                                {selected && <CheckCircle className="w-3 h-3 text-white" />}
                              </div>
                              <span className="text-xs font-medium">{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--t-border)' }}>
                <Button onClick={handleSubmit} disabled={submitting || answeredCount < questions.length}
                  className="w-full h-11 font-bold rounded-xl text-sm"
                  style={{ background: answeredCount >= questions.length ? '#0077B5' : '#64748b', color: '#fff' }}
                  data-testid="submit-linkedin-assessment">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {submitting ? 'Analyzing...' : completed ? 'Update Assessment' : 'Get My LinkedIn Score'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Optimizer Guide */}
      {completed && optimizer?.steps && optimizer.steps.length > 0 && (
        <motion.div {...fadeIn(0.1)} data-testid="linkedin-optimizer-guide">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold" style={{ color: 'var(--t-text-heading)' }}>
                Your Improvement Plan
              </h2>
              <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                {optimizer.total_steps} personalized steps based on your assessment
              </p>
            </div>
            {optimizer.linkedin_url && (
              <a href={optimizer.linkedin_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                style={{ background: 'rgba(0,119,181,0.08)', color: '#0077B5' }}
                data-testid="open-linkedin-link">
                <ExternalLink className="w-3.5 h-3.5" /> Open LinkedIn
              </a>
            )}
          </div>

          <div className="space-y-3">
            {optimizer.steps.map((step, i) => {
              const StepIcon = STEP_ICONS[step.id] || Target;
              const pColor = PRIORITY_COLORS[step.priority] || PRIORITY_COLORS.LOW;
              const isExpanded = expandedStep === step.id;

              return (
                <motion.div key={step.id} {...fadeIn(i * 0.05)}
                  className="nx-card overflow-hidden" data-testid={`optimizer-step-${step.id}`}>
                  <button onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                    className="w-full flex items-center gap-4 p-4 text-left transition-colors"
                    style={{ background: isExpanded ? `${pColor.bg}` : 'transparent' }}
                    data-testid={`toggle-step-${step.id}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: pColor.bg }}>
                      <StepIcon className="w-5 h-5" style={{ color: pColor.text }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-sm font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>
                          {step.title}
                        </h3>
                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{ background: pColor.bg, color: pColor.text, border: `1px solid ${pColor.border}` }}>
                          {step.priority}
                        </span>
                      </div>
                      <p className="text-[11px]" style={{ color: 'var(--t-text-muted)' }}>
                        {step.impact}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {step.time && (
                        <span className="text-[10px] flex items-center gap-1" style={{ color: 'var(--t-text-muted)' }}>
                          <Clock className="w-3 h-3" /> {step.time}
                        </span>
                      )}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        style={{ color: 'var(--t-text-muted)' }} />
                    </div>
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                        className="overflow-hidden">
                        <div className="px-4 pb-4 pt-1 space-y-3"
                          style={{ borderTop: `1px solid var(--t-border)` }}>
                          {/* Current vs Target */}
                          <div className="grid grid-cols-2 gap-3 mt-3">
                            <div className="p-3 rounded-xl" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                              <p className="text-[9px] font-bold tracking-wider uppercase mb-1" style={{ color: '#DC2626' }}>Current</p>
                              <p className="text-xs" style={{ color: 'var(--t-text)' }}>{step.current}</p>
                            </div>
                            <div className="p-3 rounded-xl" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)' }}>
                              <p className="text-[9px] font-bold tracking-wider uppercase mb-1" style={{ color: '#16A34A' }}>Target</p>
                              <p className="text-xs" style={{ color: 'var(--t-text)' }}>{step.target}</p>
                            </div>
                          </div>

                          {/* Steps */}
                          <div>
                            <p className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: 'var(--t-text-muted)' }}>
                              Step-by-Step Guide
                            </p>
                            <ol className="space-y-2">
                              {step.steps?.map((s, j) => (
                                <li key={j} className="flex items-start gap-2.5">
                                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5"
                                    style={{ background: 'rgba(0,119,181,0.08)', color: '#0077B5' }}>
                                    {j + 1}
                                  </span>
                                  <p className="text-xs leading-relaxed" style={{ color: 'var(--t-text-secondary)' }}>{s}</p>
                                </li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* No Steps = Perfect Score */}
      {completed && optimizer && optimizer.steps?.length === 0 && (
        <motion.div {...fadeIn(0.1)} className="nx-card p-8 text-center" data-testid="linkedin-perfect-score">
          <CheckCircle className="w-12 h-12 mx-auto mb-4" style={{ color: '#16A34A' }} />
          <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--t-text-heading)' }}>
            Your LinkedIn Profile is Excellent!
          </h2>
          <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
            You've scored perfectly across all areas. Keep maintaining your profile and stay active.
          </p>
        </motion.div>
      )}
      </div>
      )}
    </div>
  );
}
